import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';

export interface Sale {
  id: number;
  customerName: string;
  customerEmail: string;
  items: SaleItem[];
  total: number;
  status: 'pending' | 'completed' | 'cancelled';
  paymentMethod: string;
  createdAt: Date;
  salesperson: string;
}

export interface SaleItem {
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

/**
 * Sales management component - modernized with Angular 20 patterns
 * Uses signals for reactive state management and Tailwind CSS for styling
 */
@Component({
  selector: 'app-sales',
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
})
export class SalesComponent implements OnInit {
  private fb = inject(FormBuilder);
  
  // Signals for reactive state management
  sales = signal<Sale[]>([]);
  isLoading = signal(false);
  
  // Filter form
  filterForm = this.fb.nonNullable.group({
    search: [''],
    status: ['all'],
  });

  // Status options for dropdown
  statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'pending', label: 'Pendente' },
    { value: 'completed', label: 'Concluída' },
    { value: 'cancelled', label: 'Cancelada' },
  ];

  // Computed signals for derived state
  filteredSales = computed(() => {
    const salesData = this.sales();
    const searchTerm = this.filterForm.value.search?.toLowerCase() || '';
    const selectedStatus = this.filterForm.value.status || 'all';

    return salesData.filter((sale) => {
      const matchesSearch =
        sale.customerName.toLowerCase().includes(searchTerm) ||
        sale.customerEmail.toLowerCase().includes(searchTerm) ||
        sale.id.toString().includes(searchTerm);
      
      const matchesStatus =
        selectedStatus === 'all' || sale.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  });

  // Stats computed signals
  completedSales = computed(() => 
    this.sales().filter(sale => sale.status === 'completed')
  );

  totalSales = computed(() => this.completedSales().length);

  totalRevenue = computed(() => 
    this.completedSales().reduce((sum, sale) => sum + sale.total, 0)
  );

  averageTicket = computed(() => {
    const total = this.totalSales();
    return total > 0 ? this.totalRevenue() / total : 0;
  });

  resultCount = computed(() => this.filteredSales().length);

  showEmptyState = computed(() => 
    !this.isLoading() && this.filteredSales().length === 0
  );

  ngOnInit() {
    this.loadSales();
    this.setupFormSubscriptions();
  }

  private setupFormSubscriptions() {
    // Watch for form changes to trigger filtering
    this.filterForm.valueChanges.subscribe(() => {
      // Filtering is automatic through computed signals
    });
  }

  loadSales() {
    this.isLoading.set(true);
    
    // Simulating API call with mock data
    setTimeout(() => {
      const mockSales: Sale[] = [
        {
          id: 1001,
          customerName: 'João Silva',
          customerEmail: 'joao@email.com',
          items: [
            {
              productId: 1,
              productName: 'Notebook Dell Inspiron 15',
              quantity: 1,
              unitPrice: 2899.99,
              total: 2899.99,
            },
          ],
          total: 2899.99,
          status: 'completed',
          paymentMethod: 'Cartão de Crédito',
          createdAt: new Date('2024-06-25T10:30:00'),
          salesperson: 'Maria Santos',
        },
        {
          id: 1002,
          customerName: 'Ana Costa',
          customerEmail: 'ana@email.com',
          items: [
            {
              productId: 2,
              productName: 'Mouse Wireless Logitech',
              quantity: 2,
              unitPrice: 89.9,
              total: 179.8,
            },
            {
              productId: 3,
              productName: 'Teclado Mecânico',
              quantity: 1,
              unitPrice: 299.99,
              total: 299.99,
            },
          ],
          total: 479.79,
          status: 'pending',
          paymentMethod: 'Cartão de Débito',
          createdAt: new Date('2024-06-24T14:15:00'),
          salesperson: 'Pedro Lima',
        },
        {
          id: 1003,
          customerName: 'Carlos Rodrigues',
          customerEmail: 'carlos@email.com',
          items: [
            {
              productId: 4,
              productName: 'Monitor 24" Full HD',
              quantity: 1,
              unitPrice: 899.99,
              total: 899.99,
            },
          ],
          total: 899.99,
          status: 'cancelled',
          paymentMethod: 'PIX',
          createdAt: new Date('2024-06-23T16:45:00'),
          salesperson: 'Maria Santos',
        },
        {
          id: 1004,
          customerName: 'Fernanda Oliveira',
          customerEmail: 'fernanda@email.com',
          items: [
            {
              productId: 5,
              productName: 'Smartphone Samsung Galaxy',
              quantity: 1,
              unitPrice: 1299.99,
              total: 1299.99,
            },
          ],
          total: 1299.99,
          status: 'completed',
          paymentMethod: 'Cartão de Crédito',
          createdAt: new Date('2024-06-24T09:20:00'),
          salesperson: 'Carlos Oliveira',
        },
      ];

      this.sales.set(mockSales);
      this.isLoading.set(false);
    }, 1000);
  }

  // Sales actions
  viewSale(sale: Sale) {
    console.log('Viewing sale:', sale);
    // TODO: Open sale details dialog or navigate to detail page
  }

  editSale(sale: Sale) {
    console.log('Editing sale:', sale);
    // TODO: Open edit dialog or navigate to edit page
  }

  deleteSale(sale: Sale) {
    console.log('Deleting sale:', sale);
    // TODO: Show confirmation dialog and delete
  }

  newSale() {
    console.log('Creating new sale');
    // TODO: Navigate to new sale page
  }

  // Utility methods
  getStatusClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed':
        return 'Concluída';
      case 'pending':
        return 'Pendente';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
